<template lang="pug">
.page.page-dialogue-detail
  rail-layout(v-if="dialogue")
    template(#side)
      router-link.back-link(:to="indexPath") ← {{ $t('dialogues.backToIndex') }}
      .side-meta
        span.no(v-if="dialogue.no") No. {{ dialogue.no }}
        span.date {{ dialogue.date }}
        span.occasion(v-if="pick(dialogue.occasion)") {{ pick(dialogue.occasion) }}
        span.format(v-if="dialogue.format === 'edited-notes'") {{ $t('dialogues.editedNotes') }}
        span.rounds(v-if="dialogue.rounds") {{ dialogue.rounds }} {{ $t('dialogues.roundsUnit') }}
        span.duration(v-if="pick(dialogue.duration)") {{ pick(dialogue.duration) }}
      .participants
        .participant(v-for="p in dialogue.participants", :key="p.id", :class="'participant-' + p.id")
          span.p-name {{ pick(p.name) }}
          span.p-role(v-if="pick(p.role)") {{ pick(p.role) }}
      outline-nav(:title="$t('dialogues.tableOfContents')", :items="outline", :active-id="activeId")
      .side-tools
        .exports
          span.section-heading {{ $t('dialogues.exports') }}
          a.pill(:href="'/dialogues/' + dialogue.slug + '.' + currentSourceLang + '.md'", download) Markdown
          a.pill(:href="'/dialogues/' + dialogue.slug + '.json'", download) JSON
        copy-button(:text="citation", :label="$t('dialogues.copyCite')", :copied-label="$t('dialogues.copied')", :fallback-label="$t('dialogues.copyFallback')")

    article.dialogue
      .locale-notice(v-if="!hasCurrentLocale") {{ localeNoticeText }}
      header.dialogue-header
        h1 {{ localizedTitle }}
        p.subtitle(v-if="localizedSubtitle") {{ localizedSubtitle }}

      section.health-card(v-if="dialogue.health", :aria-label="healthText.title")
        .health-row
          span.tag.stage-pill(:class="'stage-' + dialogue.health.stage") {{ healthText.stage[dialogue.health.stage] }}
          span.tag.era-pill {{ healthText.era[dialogue.health.era] }}
          span.tag.score-pill {{ healthText.score }} {{ dialogue.health.score }}
        .share-bar(role="img", :aria-label="shareAria")
          span.seg(v-for="s in shareSegments", :key="s.key", :class="'seg-' + s.key", :style="{ width: s.pct + '%' }")
        ul.share-legend
          li(v-for="s in shareSegments", :key="'l-' + s.key")
            span.dot(:class="'seg-' + s.key")
            | {{ s.label }} {{ s.pct }}%
        p.stage-notice(v-if="dialogue.health.stage === 'seed'") {{ healthText.seedNotice }}
        details.health-details
          summary {{ healthText.details }}
          ul.checks
            li(v-for="c in dialogue.health.checks", :key="c.id", :class="{ ok: c.ok }") {{ c.ok ? '✓' : '✗' }} {{ healthText.checks[c.id] }} · {{ c.weight }}
          p.method {{ healthText.method }}
          p.method(v-if="dialogue.health.edited") {{ healthText.edited }}

      section.dialogue-context(v-if="contextParagraphs.length")
        h2.section-heading {{ $t('dialogues.whatIsThis') }}
        p(v-for="(para, i) in contextParagraphs", :key="'ctx-' + i") {{ para }}
        ul.how-to-read(v-if="howToRead.length")
          li(v-for="(item, i) in howToRead", :key="'how-' + i") {{ item }}

      section.callout.through-line(v-if="localizedSummary")
        h2.section-heading {{ $t('dialogues.throughLine') }}
        p.callout-text {{ localizedSummary }}
        p.callout-note(v-if="localizedSummaryNote") {{ localizedSummaryNote }}

      .dialogue-body(ref="body", :class="{ 'reviewed-dialogue': reviewedPresentation }", v-html="localizedHtml")

      footer.dialogue-footer
        p.credits
          span.credit(v-for="p in dialogue.participants", :key="'c-' + p.id") {{ pick(p.role) }}：{{ pick(p.name) }}
        p.source(v-if="pick(dialogue.source)") {{ $t('dialogues.source') }}：{{ pick(dialogue.source) }}
        p.dates
          span {{ $t('dialogues.conversationDate') }}：{{ dialogue.date }}
          span(v-if="dialogue.published") ｜{{ $t('dialogues.publishedDate') }}：{{ dialogue.published }}
        .cite-block
          .section-heading {{ $t('dialogues.cite') }}
          p.cite-text {{ citation }}
          copy-button(:text="citation", :label="$t('dialogues.copyCite')", :copied-label="$t('dialogues.copied')", :fallback-label="$t('dialogues.copyFallback')")
        p.permalink-hint {{ $t('dialogues.permalinkHint') }}

      dialogue-work-links(:slug="dialogue.slug", :zh="isZh")

      site-links(:inset="true")

      nav.dialogue-nav(v-if="prev || next")
        router-link.nav-prev(v-if="prev", :to="detailPath(prev.slug)")
          span.label ← {{ $t('dialogues.previous') }}
          span.title {{ titleFor(prev) }}
        router-link.nav-next(v-if="next", :to="detailPath(next.slug)")
          span.label {{ $t('dialogues.next') }} →
          span.title {{ titleFor(next) }}

  .container(v-else)
    .row.justify-content-center.pt-5
      .col-md-10.text-center
        p {{ $t('common.loading') }}
</template>

<script>
import SiteLinks from '@/components/ui/SiteLinks.vue'
import { computed, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useActiveHeading } from '@/composables/useActiveHeading'
import { loadContent, peekContent } from '@/runtime/content-preload'
import { unwrapZhEdges, ogTitle } from '@/runtime/page-title.mjs'
import indexDialogues from '@/data/dialogues.json'
import DialogueWorkLinks from '@/components/work/DialogueWorkLinks.vue'

const SITE = 'https://cheyuwu.com'

export default {
  name: 'PageDialogueDetail',
  components: { DialogueWorkLinks, SiteLinks },
  setup() {
    const { t, locale } = useI18n()
    const route = useRoute()
    const dialogue = ref(null)
    const body = ref(null)
    const { activeId, observe } = useActiveHeading('.round-heading, .chapter-heading', body)

    const isZh = computed(() => route.path.startsWith('/zh/') || locale.value === 'zh')
    const lang = computed(() => (isZh.value ? 'zh' : 'en'))
    const pick = (value) => {
      if (value == null) return ''
      if (typeof value === 'string') return value
      return value[lang.value] || value.zh || value.en || ''
    }
    const canonicalUrl = computed(() => SITE + (isZh.value ? '/zh' : '') + '/dialogues/' + (dialogue.value?.slug || '') + '/')
    // 中文標題首尾不留括號（2026-10-18，src/runtime/page-title.mjs）；JSON-LD 的 name 與頁面 H1 仍用原標題
    const headTitle = computed(() => (dialogue.value ? (isZh.value ? unwrapZhEdges(pick(dialogue.value.title)) : pick(dialogue.value.title)) : t('dialogues.title')))
    const headDescription = computed(() => {
      if (!dialogue.value) return ''
      const text = pick(dialogue.value.summary) || pick(dialogue.value.subtitle) || ''
      return text.length > 155 ? text.slice(0, 152) + '…' : text
    })
    const citation = computed(() => {
      const d = dialogue.value
      if (!d) return ''
      const year = String(d.date || '').slice(0, 4)
      const no = d.no ? ` No. ${d.no}` : ''
      if (isZh.value) return `${d.participants.map(p => pick(p.name)).join('、')}（${year}）。《${pick(d.title)}》。對話錄${no}，cheyuwu.com。${canonicalUrl.value}`
      return `${d.participants.map(p => pick(p.name)).join(' & ')} (${year}). "${pick(d.title)}". Dialogues${no}, cheyuwu.com. ${canonicalUrl.value}`
    })

    // Structured data: Article (rich result) whose body is a recorded Conversation, plus breadcrumbs
    const jsonLd = computed(() => {
      const d = dialogue.value
      if (!d) return ''
      const people = d.participants.map(p => ({ '@type': p.kind === 'software' || /^(muse|gemini|claude|chatgpt)$/i.test(p.id) ? 'SoftwareApplication' : 'Person', name: pick(p.name), ...(p.url ? { url: p.url } : {}) }))
      const inLanguage = lang.value === 'zh' ? 'zh-Hant' : 'en'
      const article = {
        '@type': 'Article',
        headline: pick(d.title),
        alternativeHeadline: pick(d.subtitle) || undefined,
        description: pick(d.summary) || undefined,
        datePublished: d.published || d.date,
        dateModified: d.updated || d.published || d.date,
        inLanguage,
        author: d.authors ? people.filter((p, i) => d.authors.includes(d.participants[i].id)) : (d.format === 'edited-notes' ? undefined : people.filter(p => p['@type'] === 'Person')),
        dateCreated: d.date,
        contributor: people,
        publisher: { '@type': 'Person', '@id': SITE + '/#person', name: '吳哲宇 Che-Yu Wu', url: SITE },
        mainEntityOfPage: canonicalUrl.value,
        url: canonicalUrl.value,
        image: route.meta?.publicEntry?.image || SITE + '/static/img/og-2026-10.jpg',
        keywords: (d.tags || []).join(', ') || undefined,
        hasPart: d.format === 'edited-notes' ? undefined : { '@type': 'Conversation', name: pick(d.title), dateCreated: d.date, participant: people, inLanguage },
      }
      const breadcrumb = {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Che-Yu Wu', item: SITE + (isZh.value ? '/zh/' : '/') },
          { '@type': 'ListItem', position: 2, name: isZh.value ? '思想' : 'Thinking', item: SITE + (isZh.value ? '/zh/thinking/' : '/thinking/') },
          { '@type': 'ListItem', position: 3, name: isZh.value ? '對話錄' : 'Dialogues', item: SITE + (isZh.value ? '/zh/dialogues/' : '/dialogues/') },
          { '@type': 'ListItem', position: 4, name: pick(d.title), item: canonicalUrl.value },
        ],
      }
      return JSON.stringify({ '@context': 'https://schema.org', '@graph': [article, breadcrumb] })
    })

    useHead({
      title: headTitle,
      meta: computed(() => [
        { name: 'description', content: headDescription.value },
        { property: 'og:title', content: ogTitle(headTitle.value, isZh.value ? 'zh' : 'en') },
        { property: 'og:description', content: headDescription.value },
        { property: 'og:type', content: 'article' },
        ...(dialogue.value?.published ? [{ property: 'article:published_time', content: dialogue.value.published }] : []),
      ]),
      link: computed(() => [{ rel: 'canonical', href: canonicalUrl.value }]),
      script: computed(() => (jsonLd.value ? [{ type: 'application/ld+json', innerHTML: jsonLd.value }] : [])),
    })

    // "Copy link" on every round heading (the body is v-html, so decorate after render)
    const decorateRounds = () => {
      const headings = body.value?.querySelectorAll?.('.round-heading, .chapter-heading') || []
      headings.forEach(h => {
        if (h.querySelector('.round-copy')) return
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'round-copy'
        btn.setAttribute('aria-label', `${t('dialogues.copyLink')}：${h.textContent.trim()}`)
        btn.title = t('dialogues.copyLink')
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.1 0l3-3a5 5 0 0 0-7.1-7.1l-1.7 1.7M14 11a5 5 0 0 0-7.1 0l-3 3a5 5 0 0 0 7.1 7.1l1.7-1.7"/></svg>'
        const feedback = document.createElement('span')
        feedback.className = 'copy-feedback'
        feedback.setAttribute('role', 'status')
        feedback.setAttribute('aria-live', 'polite')
        btn.appendChild(feedback)
        btn.addEventListener('click', async () => {
          const url = canonicalUrl.value + '#' + h.id
          feedback.textContent = ''
          delete btn.dataset.copied
          try {
            await navigator.clipboard.writeText(url)
            feedback.textContent = t('dialogues.copied')
            btn.dataset.copied = 'true'
            setTimeout(() => { feedback.textContent = ''; delete btn.dataset.copied }, 1800)
          } catch (e) { window.prompt(t('dialogues.copyFallback'), url) }
        })
        h.appendChild(btn)
      })
    }
    // 內文通常已在導航前載好（content-preload）：同步放進去，頁面第一次繪製就是完整對話
    const loadDialogue = async (slug) => {
      const ready = peekContent('dialogue', slug)
      dialogue.value = ready || null
      try {
        if (!ready) dialogue.value = await loadContent('dialogue', slug)
        await nextTick()
        decorateRounds()
        observe()
      } catch (err) {
        console.error('Dialogue not found:', slug, err)
        dialogue.value = null
      }
    }

    return { t, locale, dialogue, body, pick, citation, canonicalUrl, activeId, loadDialogue }
  },
  data() {
    return { indexDialogues }
  },
  computed: {
    isZh() { return this.$route.path.startsWith('/zh/') || this.locale === 'zh' },
    currentLang() { return this.isZh ? 'zh' : 'en' },
    indexPath() { return this.isZh ? '/zh/dialogues/' : '/dialogues/' },
    hasCurrentLocale() {
      if (!this.dialogue?.available_in) return true
      return this.dialogue.available_in.includes(this.currentLang)
    },
    localeNoticeText() {
      return this.isZh ? '此篇尚無中文版，以英文原文呈現。' : 'No English version yet. Showing the Chinese original.'
    },
    // 透明度卡（scripts/lib/dialogue-health.mjs 量的 health 欄位）。2026-10-03 哲宇：讓讀者看得到資料的實際狀況
    healthText() {
      return this.isZh ? {
        title: '這篇紀錄的透明度與健檢',
        stage: { seed: '🌱 育種期', growing: '🌿 生長期', mature: '🌳 成熟' },
        era: { 'personal-notes': '個人筆記期', 'early-ai': 'AI 對話早期', 'co-writing': 'Muse 共筆期', interview: '逐輪訪談期' },
        score: '健檢',
        share: { cheyu: '哲宇原話', muse: 'Muse', ai: '其他 AI', other: '其他人', editorial: '整理文字（未逐句標示）' },
        seedNotice: '這篇是想法的種子，以 AI 整理為主，哲宇的原話還不多。之後會用訪談把它補完整。',
        details: '這些數字怎麼來的',
        checks: { attribution: '標示了發言者', source: '有來源說明', complete: '沒有待續的段落', clean: '沒有系統內部痕跡', structure: '有章節結構', length: '篇幅足夠（800 字以上）' },
        method: '比例依正文字數計算：明確標示為哲宇說的話、Muse 與其他 AI 的話、其他人，以及沒有逐句標示發言者的整理文字。由網站自動量測，內容更新就重算。',
        edited: '部分段落的歸屬依編輯標註：讀過全文後，標明了整理文字或某些章節是誰寫的。',
      } : {
        title: 'Transparency and health check for this record',
        stage: { seed: '🌱 Seedling', growing: '🌿 Growing', mature: '🌳 Mature' },
        era: { 'personal-notes': 'Personal notes', 'early-ai': 'Early AI dialogues', 'co-writing': 'Co-written with Muse', interview: 'Round-by-round interviews' },
        score: 'Health',
        share: { cheyu: "Che-Yu's own words", muse: 'Muse', ai: 'Other AI', other: 'Others', editorial: 'Editorial notes (not attributed line by line)' },
        seedNotice: "This page is the seed of an idea, mostly organized by AI, with few of Che-Yu's own words so far. It will be filled out through future interviews.",
        details: 'How these numbers are made',
        checks: { attribution: 'Speakers are marked', source: 'Source is explained', complete: 'No unfinished sections', clean: 'No internal system traces', structure: 'Has section structure', length: 'Long enough (800+ characters)' },
        method: "Shares are by character count of the main text: words clearly marked as Che-Yu's, words by Muse and other AI, other people, and editorial notes not attributed line by line. Measured automatically by the site and recalculated whenever the content changes.",
        edited: 'Some attributions follow editorial annotation: after reading the full text, an editor marked who wrote the notes or certain sections.',
      }
    },
    shareSegments() {
      const s = this.dialogue?.health?.shares || {}
      return ['cheyu', 'muse', 'ai', 'other', 'editorial'].filter(k => s[k] > 0).map(k => ({ key: k, pct: s[k], label: this.healthText.share[k] }))
    },
    shareAria() { return this.shareSegments.map(s => `${s.label} ${s.pct}%`).join('，') },
    localizedTitle() { return this.pick(this.dialogue?.title) },
    localizedSubtitle() { return this.pick(this.dialogue?.subtitle) },
    reviewedPresentation() { return this.dialogue?.reviewedLanguages?.includes(this.currentSourceLang) || false },
    localizedSummary() { return this.reviewedPresentation ? '' : this.pick(this.dialogue?.summary) },
    localizedSummaryNote() { return this.pick(this.dialogue?.summary_note) },
    // Page header already owns the single <h1>; demote any markdown-level h1 in the body to h2
    localizedHtml() { return this.pick(this.dialogue?.html).replace(/<(\/?)h1(?=[\s>])/gi, '<$1h2') },
    currentSourceLang() {
      const a = this.dialogue?.available_in || ['zh']
      return a.includes(this.currentLang) ? this.currentLang : a[0]
    },
    contextParagraphs() {
      if (this.reviewedPresentation) return []
      const text = this.pick(this.dialogue?.context)
      return text ? text.split(/\n+/).map(s => s.trim()).filter(Boolean) : []
    },
    howToRead() {
      const v = this.dialogue?.how_to_read
      if (!v) return []
      return v[this.currentLang] || v.zh || v.en || []
    },
    outline() {
      const v = this.dialogue?.toc
      const toc = v ? (v[this.currentLang] || v.zh || v.en || []) : []
      return toc.map(t => ({ id: t.id, label: t.label || this.$t('dialogues.round', { n: t.n }), sub: t.question }))
    },
    localeFilteredIndex() {
      return this.indexDialogues.filter(d => !d.available_in || d.available_in.includes(this.currentLang) || d.available_in.includes('zh'))
    },
    currentIndex() { return this.localeFilteredIndex.findIndex(d => d.slug === this.$route.params.slug) },
    // Index is newest-first, so "previous" is the older one.
    prev() {
      if (this.currentIndex < 0 || this.currentIndex >= this.localeFilteredIndex.length - 1) return null
      return this.localeFilteredIndex[this.currentIndex + 1]
    },
    next() {
      if (this.currentIndex <= 0) return null
      return this.localeFilteredIndex[this.currentIndex - 1]
    },
  },
  watch: {
    '$route.params.slug': { immediate: true, handler(slug) { if (slug) this.loadDialogue(slug) } },
  },
  methods: {
    titleFor(d) { return d ? this.pick(d.title) : '' },
    detailPath(slug) {
      const d = this.indexDialogues.find(d => d.slug === slug)
      const lang = d?.available_in.includes(this.currentLang) ? this.currentLang : d?.available_in[0]
      return `${lang === 'zh' ? '/zh' : ''}/dialogues/${slug}/`
    },
  },
}
</script>

<style lang="sass">
.page-dialogue-detail
  background-color: #000
  color: #fff
  min-height: 100vh
  text-align: left
  --cheyu: var(--gold)
  --muse: var(--muse-violet)

  .participants
    display: flex
    flex-wrap: wrap
    gap: 8px

  .side-tools
    border-top: 1px solid var(--line)
    padding-top: 14px
    display: flex
    flex-direction: column
    gap: 10px
    align-items: flex-start
  .exports
    display: flex
    flex-wrap: wrap
    gap: 8px
    align-items: center
    .section-heading
      margin: 0

  .dialogue-header
    margin-bottom: 36px
    padding-bottom: 24px
    border-bottom: 1px solid rgba(255,255,255,0.1)
    h1
      font-family: var(--font-display)
      font-size: 2.6rem
      font-weight: 600
      line-height: 1.3
      margin-bottom: 14px
    .subtitle
      font-size: 1.12rem
      color: #aaa
      line-height: 1.6
      margin: 0

  .health-card
    margin: 0 0 40px
    padding: 22px 24px 18px
    border: 0
    border-radius: var(--r-lg)
    background: var(--bg1)
    font-size: 0.86rem
    color: var(--ink-2)
    .health-row
      display: flex
      flex-wrap: wrap
      gap: 8px
      margin-bottom: 14px
    // 階段／時期／分數：共用 .tag（色點＋文字）；色點只用來分階段
    .stage-seed, .stage-growing
      --tag-c: var(--k-sketch)
    .stage-mature
      --tag-c: var(--gold)
    .share-bar
      display: flex
      height: 8px
      border-radius: 4px
      overflow: hidden
      background: rgba(255,255,255,0.06)
      margin-bottom: 10px
    .seg-cheyu
      background: var(--cheyu, #d9c267)
    .seg-muse
      background: var(--muse, #a088cc)
    .seg-ai
      background: #6f9fc4
    .seg-other
      background: #8fbf8f
    .seg-editorial
      background: #4a4a47
    .share-legend
      display: flex
      flex-wrap: wrap
      gap: 4px 16px
      list-style: none
      padding: 0
      margin: 0 0 6px
      li
        display: inline-flex
        align-items: center
        gap: 6px
      .dot
        width: 8px
        height: 8px
        border-radius: 50%
        display: inline-block
    .stage-notice
      margin: 10px 0 0
      color: #9fd3ac
    .health-details
      margin-top: 10px
      summary
        cursor: pointer
        color: #8e8e89
        font-size: 0.8rem
      .checks
        list-style: none
        padding: 0
        margin: 10px 0 6px
        li
          color: #8e8e89
          line-height: 1.8
        li.ok
          color: #b9b9b5
      .method
        margin: 6px 0 0
        color: #8e8e89
        font-size: 0.8rem
        line-height: 1.7
  .dialogue-context
    margin-bottom: 40px
    p
      font-size: 1rem
      line-height: 1.85
      color: #c4c4c0
      margin-bottom: 14px
    .how-to-read
      margin: 10px 0 0
      padding-left: 1.3em
      color: #9a9a96
      font-size: 0.95rem
      line-height: 1.75
      li
        margin-bottom: 6px

  .through-line
    margin-bottom: 44px

  .dialogue-body
    min-width: 0
    max-width: 100%
    overflow-wrap: anywhere
    pre
      max-width: 100%
      overflow-x: auto
      padding: 18px 20px
      white-space: pre
      background: var(--bg1)
      border: 0
      border-radius: var(--r-md)
      font-size: 0.86rem
      line-height: 1.65
      // 不寫字色會吃到 Bootstrap 的 pre 預設（#212529），黑底上幾乎看不見（2026-10-03 幸福資產負債表的公式框）
      color: var(--ink-2)
      code
        color: inherit
        white-space: inherit
        overflow-wrap: normal
    img, video, iframe
      max-width: 100%
    h1
      font-family: var(--font-display)
      font-size: 1.6rem
      line-height: 1.5
      margin: 48px 0 24px
      color: #fff
    font-size: 1.05rem
    line-height: 1.9
    color: #ddd
    h2
      font-family: var(--font-display)
      font-size: 1.5rem
      font-weight: 600
      color: #fff
      margin-top: 72px
      margin-bottom: 20px
      padding-top: 24px
      border-top: 1px solid rgba(255,255,255,0.1)
    h3.round-heading
      font-size: 0.82rem
      letter-spacing: 0.14em
      text-transform: uppercase
      color: #777
      font-weight: 600
      margin-top: 56px
      margin-bottom: 18px
      scroll-margin-top: 90px
      display: flex
      align-items: center
      gap: 12px
      a
        color: inherit
        text-decoration: none
        &:hover
          color: #aaa
    .chapter-heading
      display: flex
      align-items: baseline
      gap: 12px
      scroll-margin-top: 90px
    .round-copy
      position: relative
      display: inline-flex
      flex: 0 0 32px
      width: 32px
      height: 32px
      align-items: center
      justify-content: center
      align-self: baseline
      padding: 0
      background: transparent
      border: 0
      border-radius: 50%
      color: var(--ink-3)
      cursor: pointer
      vertical-align: middle
      transition: var(--t-hover)
      svg
        display: block
        flex: none
      &:hover, &[data-copied="true"]
        color: var(--gold, #d9c267)
        background: rgba(217,194,103,0.08)
      &:focus-visible
        color: #fff
        outline: 2px solid var(--gold, #d9c267)
        outline-offset: 3px
      .copy-feedback
        position: absolute
        bottom: calc(100% + 6px)
        left: 50%
        transform: translateX(-50%)
        white-space: nowrap
        font-family: var(--font-body)
        font-size: 11px
        line-height: 1.5
        font-weight: 400
        letter-spacing: normal
        text-transform: none
        color: var(--gold, #d9c267)
        pointer-events: none
    p
      margin-bottom: 20px
    .speaker
      display: inline-block
      font-size: 0.8rem
      font-weight: 600
      letter-spacing: 0.08em
      padding: 2px 12px
      border-radius: var(--r-pill)
      margin-right: 10px
      vertical-align: 0.12em
      border: 0
    .turn-muse .speaker, .speaker-muse .speaker
      color: var(--muse)
      background: var(--violet-tint)
    .turn-cheyu .speaker, .speaker-cheyu .speaker
      color: var(--cheyu)
      background: var(--gold-tint)
    .turn-muse
      font-size: 1.08rem
      color: #f4f4f2
      margin-bottom: 18px
      .turn-text
        font-weight: 500
    .speaker-line
      margin-bottom: 10px
    // 編註：同 editorial 的 .soft-panel（內文是 build 產的 HTML，不能直接掛 class），標籤前一顆 Muse 紫點
    .note
      margin: 26px 0 34px
      padding: 18px 22px
      border: 0
      background: var(--tint-1)
      color: var(--ink-2)
      font-size: 0.95rem
      line-height: 1.8
      border-radius: var(--r-md)
      .note-label
        display: flex
        align-items: center
        gap: 8px
        font-size: 0.75rem
        letter-spacing: 0.12em
        text-transform: uppercase
        color: var(--muse)
        margin-bottom: 8px
        &::before
          content: ''
          flex: none
          width: 6px
          height: 6px
          border-radius: 50%
          background: var(--muse-violet)
      p
        margin-bottom: 10px
        &:last-child
          margin-bottom: 0
      a
        color: #c9bde6
    a
      color: var(--gold)
      text-decoration: underline
      text-decoration-color: rgba(217,194,103,0.4)
      &:hover
        text-decoration-color: var(--gold)
    ul, ol
      margin-bottom: 20px
      padding-left: 1.4em
      li
        margin-bottom: 8px
    table
      display: block
      max-width: 100%
      overflow-x: auto
      width: 100%
      border-collapse: collapse
      margin: 28px 0
      font-size: 0.95rem
      // 線與表頭樣式由 editorial.sass 接管（.dialogue-body table）
      th, td
        min-width: 140px
        line-height: 1.6
    hr
      border: 0
      border-top: 1px solid rgba(255,255,255,0.1)
      margin: 48px 0
    // 引言樣式由 editorial.sass 接管（.dialogue-body blockquote）
    blockquote
      p
        margin-bottom: 8px
        &:last-child
          margin-bottom: 0

  .dialogue-footer
    margin-top: 64px
    padding-top: 24px
    border-top: 1px solid rgba(255,255,255,0.1)
    font-size: 0.9rem
    color: #888
    p
      margin-bottom: 6px
    .credit + .credit::before
      content: '｜'
      margin: 0 8px
      color: #555
    .cite-block
      margin-top: 22px
      padding: 18px 22px
      border: 0
      border-radius: var(--r-md)
      background: var(--tint-1)
      .cite-text
        color: var(--ink-2)
        font-size: 0.88rem
        line-height: 1.7
        word-break: break-all
        margin-bottom: 8px
    .permalink-hint
      margin-top: 12px
      font-size: 0.82rem
      color: #666

  .dialogue-nav
    display: flex
    justify-content: space-between
    gap: 24px
    margin-top: 48px
    padding-top: 24px
    border-top: 1px solid rgba(255,255,255,0.08)
    a
      flex: 1
      display: flex
      flex-direction: column
      gap: 4px
      padding: 18px 22px
      border: 0
      border-radius: var(--r-lg)
      background: var(--bg1)
      color: var(--ink)
      text-decoration: none
      transition: var(--t-hover)
      max-width: 48%
      &:hover
        background: var(--bg2)
        text-decoration: none
        .title
          color: var(--gold)
      .label
        font-size: 0.8rem
        color: var(--ink-3)
        letter-spacing: 0.05em
        text-transform: uppercase
      .title
        font-size: 0.95rem
        line-height: 1.4
        color: var(--ink-2)
        transition: var(--t-hover)
    .nav-next
      text-align: right
      margin-left: auto

  @media (max-width: 991px)
    .dialogue-header h1
      font-size: 1.9rem
    .dialogue-body
      font-size: 1rem
    .dialogue-nav
      flex-direction: column
      a
        max-width: 100%
</style>

<style>
.page-dialogue-detail .reviewed-dialogue .review-turn{position:relative;padding:6px 0 6px 104px;margin:30px 0 38px;border-top:1px solid #393630;min-width:0}
.page-dialogue-detail .reviewed-dialogue .review-turn::before{content:attr(data-review-label);position:absolute;left:0;top:12px;width:82px;font-family:var(--font-body);font-size:13px;font-weight:500;color:var(--gold);line-height:1.6}
.page-dialogue-detail .reviewed-dialogue .review-turn[data-reviewed-speaker="muse"]::before{color:var(--muse-violet)}
.page-dialogue-detail .reviewed-dialogue .review-turn>blockquote{border:0;padding:0;margin:16px 0;background:none;color:inherit}
.page-dialogue-detail .reviewed-dialogue .review-note{margin:26px 0 34px;padding:18px 22px;border:0;border-radius:var(--r-md);background:var(--tint-1);color:var(--ink-2);min-width:0}
.page-dialogue-detail .reviewed-dialogue .review-note::before{content:attr(data-review-label);display:block;font-size:11px;letter-spacing:.03em;color:#89847b;line-height:1.6;margin-bottom:12px;font-family:var(--font-body)}
.page-dialogue-detail .reviewed-dialogue .review-note>p:first-of-type{margin-top:0}
.page-dialogue-detail .reviewed-dialogue .review-inline-turn{color:#f1e9d8;background:rgba(185,156,90,.08);box-decoration-break:clone;-webkit-box-decoration-break:clone}
.page-dialogue-detail .reviewed-dialogue .review-inline-start::before{content:attr(data-review-label) "：";font-size:12px;color:var(--gold);margin-right:6px;font-family:var(--font-body)}
@media(max-width:640px){.page-dialogue-detail .reviewed-dialogue .review-turn{padding:12px 0 0;margin:28px 0}.page-dialogue-detail .reviewed-dialogue .review-turn::before{position:static;display:block;width:auto;margin-bottom:16px}.page-dialogue-detail .reviewed-dialogue .review-note{padding:14px 16px}.page-dialogue-detail .reviewed-dialogue .review-inline-start::before{font-size:11px}}
</style>
